/**
 * AudioForwarder — Captures the Realtime API's remote audio track,
 * buffers PCM samples via an AudioWorklet, and emits Uint8Array chunks
 * (~1s each) of Int16 PCM ready for Anam.ai createAgentAudioInputStream.
 *
 * Anam expects: pcm_s16le, 24kHz, mono.
 * The Realtime API outputs 24kHz PCM — exact match, no conversion needed.
 */

const TARGET_SAMPLE_RATE = 24000
const SAMPLES_PER_CHUNK = 4800 // ~200ms at 24kHz — smaller chunks for lower latency to Anam lip-sync

// ---------------------------------------------------------------------------
// Inline AudioWorklet processor (runs in a separate thread)
// ---------------------------------------------------------------------------
const WORKLET_CODE = /* js */ `
class PcmBufferProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this._buffer = new Float32Array(0)
    this._paused = false
    this._chunkSize = ${SAMPLES_PER_CHUNK}

    this.port.onmessage = (e) => {
      if (e.data.type === 'pause') this._paused = true
      if (e.data.type === 'resume') this._paused = false
    }
  }

  process(inputs) {
    const input = inputs[0]
    if (!input || !input[0] || this._paused) return true

    const samples = input[0] // Float32 mono channel

    // Append to buffer
    const newBuf = new Float32Array(this._buffer.length + samples.length)
    newBuf.set(this._buffer)
    newBuf.set(samples, this._buffer.length)
    this._buffer = newBuf

    // Emit chunks when we have enough samples
    while (this._buffer.length >= this._chunkSize) {
      const chunk = this._buffer.slice(0, this._chunkSize)
      this._buffer = this._buffer.slice(this._chunkSize)
      this.port.postMessage({ type: 'chunk', samples: chunk })
    }

    return true
  }
}

registerProcessor('pcm-buffer-processor', PcmBufferProcessor)
`

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert Float32 samples (-1..1) to Int16 PCM bytes. */
function float32ToInt16(float32: Float32Array): Int16Array {
  const int16 = new Int16Array(float32.length)
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]))
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff
  }
  return int16
}

// ---------------------------------------------------------------------------
// AudioForwarder class
// ---------------------------------------------------------------------------

export class AudioForwarder {
  /** Fired with a Uint8Array of Int16 PCM samples (~1s of audio, 24kHz mono). */
  onAudioChunk: ((pcmData: Uint8Array) => void) | null = null

  private audioCtx: AudioContext | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null
  private workletNode: AudioWorkletNode | null = null
  private _started = false
  private _paused = false

  get isStarted(): boolean {
    return this._started
  }

  get isPaused(): boolean {
    return this._paused
  }

  /**
   * Start capturing audio from the given remote MediaStream.
   * The stream typically comes from RealtimeSession.onRemoteTrack.
   */
  async start(remoteStream: MediaStream): Promise<void> {
    if (this._started) return

    // Create AudioContext at 24kHz — matches Realtime API output
    this.audioCtx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE })

    // Register the inline worklet processor via Blob URL
    const blob = new Blob([WORKLET_CODE], { type: 'application/javascript' })
    const workletUrl = URL.createObjectURL(blob)
    await this.audioCtx.audioWorklet.addModule(workletUrl)
    URL.revokeObjectURL(workletUrl)

    // Create source from remote stream
    this.sourceNode = this.audioCtx.createMediaStreamSource(remoteStream)

    // Create worklet node
    this.workletNode = new AudioWorkletNode(this.audioCtx, 'pcm-buffer-processor')

    // Handle chunks from the worklet
    this.workletNode.port.onmessage = (e) => {
      if (e.data.type === 'chunk') {
        const float32: Float32Array = e.data.samples
        const int16 = float32ToInt16(float32)
        this.onAudioChunk?.(new Uint8Array(int16.buffer as ArrayBuffer))
      }
    }

    // Connect: source → worklet → destination (destination is needed to keep the graph alive)
    this.sourceNode.connect(this.workletNode)
    // Connect worklet to a silent destination to keep the audio graph processing.
    // We DON'T connect to audioCtx.destination to avoid local playback.
    // Instead, use a GainNode set to 0 as a silent sink.
    const silentSink = this.audioCtx.createGain()
    silentSink.gain.value = 0
    silentSink.connect(this.audioCtx.destination)
    this.workletNode.connect(silentSink)

    this._started = true
    this._paused = false
    console.log('[AudioForwarder] Started capturing remote audio at 24kHz')
  }

  /** Pause chunk emission (audio is still captured but discarded). */
  pause(): void {
    if (!this._started || this._paused) return
    this._paused = true
    this.workletNode?.port.postMessage({ type: 'pause' })
    console.log('[AudioForwarder] Paused')
  }

  /** Resume chunk emission. */
  resume(): void {
    if (!this._started || !this._paused) return
    this._paused = false
    this.workletNode?.port.postMessage({ type: 'resume' })
    console.log('[AudioForwarder] Resumed')
  }

  /** Stop capturing and release all resources. */
  stop(): void {
    if (!this._started) return

    try { this.workletNode?.disconnect() } catch {}
    try { this.sourceNode?.disconnect() } catch {}
    try { this.audioCtx?.close() } catch {}

    this.workletNode = null
    this.sourceNode = null
    this.audioCtx = null
    this._started = false
    this._paused = false
    console.log('[AudioForwarder] Stopped')
  }
}
